import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  useTheme,
  Snackbar,
  Chip,
  Modal,
  Typography,
  Card,
  CardContent,
  Grid,
  Divider,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import Header from "../../components/Header";
import { tokens } from "../../theme";
import MuiAlert from "@mui/material/Alert";

const UserStripeAccounts = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [alertSeverity, setAlertSeverity] = useState("success");
  const [alertMessage, setAlertMessage] = useState("");
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedSeller, setSelectedSeller] = useState(null);

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // Mock API call - replace with your actual API endpoint
  const fetchSellers = async () => {
    setLoading(true);
    try {
      const response = await fetch("https://backend.jcgn.org/api/seller/getSellers");
      const result = await response.json();
      
      if (result.success) {
        const formattedData = result.seller.map((seller, index) => ({
          id: seller.id,
          serialNo: index + 1,
          sellername: seller.sellername,
          orgname: seller.orgname,
          sellerphone: seller.sellerphone,
          bussinesstype: seller.bussinesstype,
          delivertype: seller.delivertype,
          deliverdistance: seller.deliverdistance,
          stripeaccountID: seller.stripeaccountID,
          balance_cents: seller.balance_cents ? `$${(seller.balance_cents / 100).toFixed(2)}` : "$0.00",
          paymentmethod: seller.paymentmethod,
          description: seller.description,
          businessloction: seller.businessloction,
          photovideolink: seller.photovideolink,
          accountnumber: seller.accountnumber,
          createdAt: formatDateToReadable(seller.createdAt),
          updatedAt: formatDateToReadable(seller.updatedAt),
          UserId: seller.UserId,
          status: seller.stripeaccountID ? "Connected" : "Not Connected"
        }));
        setData(formattedData);
      } else {
        throw new Error("Failed to fetch sellers");
      }
    } catch (error) {
      console.error("Error fetching sellers:", error);
      setAlertSeverity("error");
      setAlertMessage("Failed to fetch seller data");
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const formatDateToReadable = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  useEffect(() => {
    fetchSellers();
  }, []);

  const handleViewClick = (seller) => {
    setSelectedSeller(seller);
    setViewModalOpen(true);
  };

  const handleCloseModal = () => {
    setViewModalOpen(false);
    setSelectedSeller(null);
  };

  const handleRefresh = () => {
    fetchSellers();
    setAlertSeverity("success");
    setAlertMessage("Data refreshed successfully");
    setOpenSnackbar(true);
  };

  const handleStripeConnect = (id) => {
    console.log("Connect Stripe for seller:", id);
    setAlertSeverity("success");
    setAlertMessage("Redirecting to Stripe connection...");
    setOpenSnackbar(true);
  };

  const columns = [
    { 
      field: "serialNo", 
      headerName: "S.No", 
      width: 80 
    },
    { 
      field: "sellername", 
      headerName: "Seller Name", 
      flex: 1 
    },
    { 
      field: "orgname", 
      headerName: "Organization", 
      flex: 1 
    },
    { 
      field: "sellerphone", 
      headerName: "Phone", 
      flex: 1 
    },
    { 
      field: "bussinesstype", 
      headerName: "Business Type", 
      flex: 1,
      renderCell: (params) => (
        <Chip 
          label={params.value} 
          color={params.value === "international" ? "primary" : "secondary"}
          size="small"
        />
      )
    },
    { 
      field: "stripeaccountID", 
      headerName: "Stripe Account ID", 
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={params.value || "No Stripe account connected"}>
          <span style={{ 
            fontFamily: 'monospace',
            fontSize: '12px',
            color: params.value ? colors.greenAccent[300] : colors.redAccent[500]
          }}>
            {params.value ? `${params.value.substring(0, 12)}...` : "Not Connected"}
          </span>
        </Tooltip>
      )
    },
    { 
      field: "status", 
      headerName: "Stripe Status", 
      flex: 1,
      renderCell: (params) => (
        <Chip 
          label={params.value} 
          color={params.value === "Connected" ? "success" : "error"}
          variant="outlined"
        />
      )
    },
    { 
      field: "balance_cents", 
      headerName: "Balance", 
      flex: 1,
      renderCell: (params) => (
        <span style={{ 
          fontWeight: 'bold',
          color: params.value !== "$0.00" ? colors.greenAccent[300] : 'inherit'
        }}>
          {params.value}
        </span>
      )
    },
    { 
      field: "createdAt", 
      headerName: "Created Date", 
      flex: 1 
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="View Details">
            <IconButton 
              onClick={() => handleViewClick(params.row)}
              size="small"
              sx={{ color: colors.blueAccent[300] }}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
          {!params.row.stripeaccountID && (
            <Tooltip title="Connect Stripe">
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleStripeConnect(params.row.id)}
                sx={{
                  color: colors.blueAccent[300],
                  borderColor: colors.blueAccent[300],
                  '&:hover': {
                    backgroundColor: colors.blueAccent[800],
                    borderColor: colors.blueAccent[300],
                  }
                }}
              >
                Connect
              </Button>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  // Modal style
  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '80%',
    maxWidth: '800px',
    maxHeight: '90vh',
    overflow: 'auto',
    bgcolor: 'background.paper',
    borderRadius: '8px',
    boxShadow: 24,
    p: 0,
  };

  return (
    <Box m="20px">
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        marginBottom="20px"
      >
        <Header title="User Stripe Accounts" subtitle="Manage seller Stripe connections" />
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading}
            sx={{
              color: colors.greenAccent[300],
              borderColor: colors.greenAccent[300],
              '&:hover': {
                backgroundColor: colors.greenAccent[800],
                borderColor: colors.greenAccent[300],
              }
            }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      <Box
        m="10px 0 0 0"
        height="70vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
            fontSize: "14px",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .name-column--cell": {
            color: colors.greenAccent[300],
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.greenAccent[700],
            borderBottom: "none",
            fontSize: "16px",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.greenAccent[700],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${colors.grey[100]} !important`,
          },
          "& .MuiDataGrid-row": {
            '&:hover': {
              backgroundColor: colors.primary[500],
            },
          },
        }}
      >
        <DataGrid
          rows={data}
          columns={columns}
          loading={loading}
          components={{ Toolbar: GridToolbar }}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 10,
              },
            },
          }}
          pageSizeOptions={[10, 25, 50]}
          disableRowSelectionOnClick
        />
      </Box>

      {/* View Seller Details Modal */}
      <Modal
        open={viewModalOpen}
        onClose={handleCloseModal}
        aria-labelledby="seller-details-modal"
        aria-describedby="seller-details-description"
      >
        <Box sx={modalStyle}>
          {selectedSeller && (
            <Card>
              <CardContent sx={{ p: 0 }}>
                {/* Header */}
                <Box
                  sx={{
                    backgroundColor: colors.greenAccent[700],
                    color: 'white',
                    p: 3,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant="h5" component="h2" fontWeight="bold">
                    Seller Details
                  </Typography>
                  <IconButton
                    onClick={handleCloseModal}
                    sx={{ color: 'white' }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>

                {/* Content */}
                <Box sx={{ p: 3 }}>
                  <Grid container spacing={3}>
                    {/* Basic Information */}
                    <Grid item xs={12} md={6}>
                      <Typography variant="h6" gutterBottom sx={{ color: colors.greenAccent[500] }}>
                        Basic Information
                      </Typography>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Seller Name
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {selectedSeller.sellername}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Organization
                        </Typography>
                        <Typography variant="body1">
                          {selectedSeller.orgname}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Phone
                        </Typography>
                        <Typography variant="body1">
                          {selectedSeller.sellerphone}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="textSecondary">
                          User ID
                        </Typography>
                        <Typography variant="body1">
                          {selectedSeller.UserId}
                        </Typography>
                      </Box>
                    </Grid>

                    {/* Business Information */}
                    <Grid item xs={12} md={6}>
                      <Typography variant="h6" gutterBottom sx={{ color: colors.greenAccent[500] }}>
                        Business Information
                      </Typography>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Business Type
                        </Typography>
                        <Chip 
                          label={selectedSeller.bussinesstype} 
                          color={selectedSeller.bussinesstype === "international" ? "primary" : "secondary"}
                          size="small"
                        />
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Delivery Type
                        </Typography>
                        <Typography variant="body1">
                          {selectedSeller.delivertype}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Delivery Distance
                        </Typography>
                        <Typography variant="body1">
                          {selectedSeller.deliverdistance || "N/A"}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Business Location
                        </Typography>
                        <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '12px' }}>
                          {selectedSeller.businessloction || "N/A"}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Divider />
                    </Grid>

                    {/* Payment Information */}
                    <Grid item xs={12} md={6}>
                      <Typography variant="h6" gutterBottom sx={{ color: colors.greenAccent[500] }}>
                        Payment Information
                      </Typography>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Stripe Status
                        </Typography>
                        <Chip 
                          label={selectedSeller.status} 
                          color={selectedSeller.status === "Connected" ? "success" : "error"}
                          variant="outlined"
                        />
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Stripe Account ID
                        </Typography>
                        <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '12px' }}>
                          {selectedSeller.stripeaccountID || "Not Connected"}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Account Number
                        </Typography>
                        <Typography variant="body1">
                          {selectedSeller.accountnumber || "N/A"}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Payment Method
                        </Typography>
                        <Typography variant="body1">
                          {selectedSeller.paymentmethod || "N/A"}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Current Balance
                        </Typography>
                        <Typography variant="body1" fontWeight="bold" sx={{ color: colors.greenAccent[300] }}>
                          {selectedSeller.balance_cents}
                        </Typography>
                      </Box>
                    </Grid>

                    {/* Additional Information */}
                    <Grid item xs={12} md={6}>
                      <Typography variant="h6" gutterBottom sx={{ color: colors.greenAccent[500] }}>
                        Additional Information
                      </Typography>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Description
                        </Typography>
                        <Typography variant="body1">
                          {selectedSeller.description || "No description provided"}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Photo/Video Link
                        </Typography>
                        <Typography variant="body1" sx={{ 
                          fontFamily: 'monospace', 
                          fontSize: '12px',
                          wordBreak: 'break-all'
                        }}>
                          {selectedSeller.photovideolink || "N/A"}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Created Date
                        </Typography>
                        <Typography variant="body1">
                          {selectedSeller.createdAt}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Last Updated
                        </Typography>
                        <Typography variant="body1">
                          {selectedSeller.updatedAt}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </CardContent>
            </Card>
          )}
        </Box>
      </Modal>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={5000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MuiAlert
          onClose={() => setOpenSnackbar(false)}
          severity={alertSeverity}
          elevation={6}
          variant="filled"
          style={{ color: "white" }}
        >
          {alertSeverity === "success" ? "Success" : "Error"}
          {": "}
          {alertMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
};

export default UserStripeAccounts;